<script>
import baseMixin from '@mixins/baseMixin'
import { mapGetters } from 'vuex'
import clearRetries from '@/services/signalr/retryConnection.js'

export default {
  name: 'NotificationButton',
  mixins: [baseMixin],
  computed: {
    ...mapGetters({
      hasExceededRetries: 'signalr/hasExceededRetryLimit',
      countNewMessages: 'message/countNewMessages',
      isSignalRInit: 'signalr/isSignalRInit',
      isLoading: 'signalr/isLoading',
    }),
    compIcon() {
      if (!this.isSignalRInit || this.hasExceededRetries)
        return 'mdi-cloud-off-outline'
      return 'mdi-bell'
    },
    compTooltipMessage() {
      if (this.hasExceededRetries) return 'Failed to connect. Click to retry'
      if (!this.isSignalRInit) return 'Connecting...'

      return `${this.countNewMessages} Notification${
        this.countNewMessages !== 1 ? 's' : ''
      }`
    },
  },
  methods: {
    async handleClick() {
      if (this.hasExceededRetries) {
        // Clear retry counter and attempt to reconnect
        return await clearRetries()
      }

      // Only redirect if not already on the messages page
      if (!this.$route.matched.some(({ name }) => name === 'message'))
        this.$router.push({ name: 'message' })
    },
  },
}
</script>

<template>
  <v-tooltip bottom>
    <template v-slot:activator="{ on }" class="rounded-xl">
      <span v-on="on">
        <v-btn
          id="menu-notifications-button"
          :loading="(!isSignalRInit && !hasExceededRetries) || isLoading"
          icon
          exact
          height="72"
          width="72"
          tile
          :color="hasExceededRetries ? 'red' : ''"
          @click="handleClick"
        >
          <v-badge
            :value="isSignalRInit && countNewMessages > 0 && !isLoading"
            color="red"
            :content="countNewMessages"
            :dot="hasExceededRetries"
          >
            <BaseIcon
              :class="[isSignalRInit && countNewMessages > 0 ? 'bell' : '']"
              :name="compIcon"
            ></BaseIcon>
          </v-badge>

          <template v-slot:loader>
            <div class="dot-flashing"></div>
          </template>
        </v-btn>
      </span>
    </template>
    <span>{{ compTooltipMessage }}</span>
  </v-tooltip>
</template>

<style lang="scss" scoped>
@import '@design';

$dot-width: 8px;
$dot-height: 8px;
$dot-radius: $dot-width / 2;

$dot-color: #fafafa;
$dot-bg-color: $dot-color;
$dot-before-color: $dot-color;
$dot-after-color: $dot-color;

$dot-spacing: $dot-width + $dot-width / 2;

// Sourced from https://github.com/nzbin/three-dots on 22/07/2021 11:03 AM
.dot-flashing {
  position: relative;
  width: $dot-width;
  height: $dot-height;
  color: $dot-color;
  background-color: $dot-color;
  border-radius: $dot-radius;
  animation: dotFlashing 1s infinite linear alternate;
  animation-delay: 0.5s;
}

.dot-flashing::before,
.dot-flashing::after {
  position: absolute;
  top: 0;
  display: inline-block;
  content: '';
}

.dot-flashing::before {
  left: -$dot-spacing;
  width: $dot-width;
  height: $dot-height;
  color: $dot-before-color;
  background-color: $dot-bg-color;
  border-radius: $dot-radius;
  animation: dotFlashing 1s infinite alternate;
  animation-delay: 0s;
}

.dot-flashing::after {
  left: $dot-spacing;
  width: $dot-width;
  height: $dot-height;
  color: $dot-after-color;
  background-color: $dot-bg-color;
  border-radius: $dot-radius;
  animation: dotFlashing 1s infinite alternate;
  animation-delay: 1s;
}

@keyframes dotFlashing {
  0% {
    background-color: #fafafa1a;
  }
  50%,
  100% {
    background-color: #ebe6ff;
  }
}

.bell {
  -webkit-transform-origin: 50% 4px;
  -moz-transform-origin: 50% 4px;
  -webkit-animation: ring 4s 0.7s ease-in-out 5;
  -moz-animation: ring 4s 0.7s ease-in-out 5;
  animation: ring 4s 0.7s ease-in-out 5;
}

@-webkit-keyframes ring {
  0% {
    -webkit-transform: rotateZ(0);
  }
  1% {
    -webkit-transform: rotateZ(30deg);
  }
  3% {
    -webkit-transform: rotateZ(-28deg);
  }
  5% {
    -webkit-transform: rotateZ(34deg);
  }
  7% {
    -webkit-transform: rotateZ(-32deg);
  }
  9% {
    -webkit-transform: rotateZ(30deg);
  }
  11% {
    -webkit-transform: rotateZ(-28deg);
  }
  13% {
    -webkit-transform: rotateZ(26deg);
  }
  15% {
    -webkit-transform: rotateZ(-24deg);
  }
  17% {
    -webkit-transform: rotateZ(22deg);
  }
  19% {
    -webkit-transform: rotateZ(-20deg);
  }
  21% {
    -webkit-transform: rotateZ(18deg);
  }
  23% {
    -webkit-transform: rotateZ(-16deg);
  }
  25% {
    -webkit-transform: rotateZ(14deg);
  }
  27% {
    -webkit-transform: rotateZ(-12deg);
  }
  29% {
    -webkit-transform: rotateZ(10deg);
  }
  31% {
    -webkit-transform: rotateZ(-8deg);
  }
  33% {
    -webkit-transform: rotateZ(6deg);
  }
  35% {
    -webkit-transform: rotateZ(-4deg);
  }
  37% {
    -webkit-transform: rotateZ(2deg);
  }
  39% {
    -webkit-transform: rotateZ(-1deg);
  }
  41% {
    -webkit-transform: rotateZ(1deg);
  }

  43% {
    -webkit-transform: rotateZ(0);
  }
  100% {
    -webkit-transform: rotateZ(0);
  }
}

@-moz-keyframes ring {
  0% {
    -moz-transform: rotate(0);
  }
  1% {
    -moz-transform: rotate(30deg);
  }
  3% {
    -moz-transform: rotate(-28deg);
  }
  5% {
    -moz-transform: rotate(34deg);
  }
  7% {
    -moz-transform: rotate(-32deg);
  }
  9% {
    -moz-transform: rotate(30deg);
  }
  11% {
    -moz-transform: rotate(-28deg);
  }
  13% {
    -moz-transform: rotate(26deg);
  }
  15% {
    -moz-transform: rotate(-24deg);
  }
  17% {
    -moz-transform: rotate(22deg);
  }
  19% {
    -moz-transform: rotate(-20deg);
  }
  21% {
    -moz-transform: rotate(18deg);
  }
  23% {
    -moz-transform: rotate(-16deg);
  }
  25% {
    -moz-transform: rotate(14deg);
  }
  27% {
    -moz-transform: rotate(-12deg);
  }
  29% {
    -moz-transform: rotate(10deg);
  }
  31% {
    -moz-transform: rotate(-8deg);
  }
  33% {
    -moz-transform: rotate(6deg);
  }
  35% {
    -moz-transform: rotate(-4deg);
  }
  37% {
    -moz-transform: rotate(2deg);
  }
  39% {
    -moz-transform: rotate(-1deg);
  }
  41% {
    -moz-transform: rotate(1deg);
  }

  43% {
    -moz-transform: rotate(0);
  }
  100% {
    -moz-transform: rotate(0);
  }
}

@keyframes ring {
  0% {
    transform: rotate(0);
  }
  1% {
    transform: rotate(30deg);
  }
  3% {
    transform: rotate(-28deg);
  }
  5% {
    transform: rotate(34deg);
  }
  7% {
    transform: rotate(-32deg);
  }
  9% {
    transform: rotate(30deg);
  }
  11% {
    transform: rotate(-28deg);
  }
  13% {
    transform: rotate(26deg);
  }
  15% {
    transform: rotate(-24deg);
  }
  17% {
    transform: rotate(22deg);
  }
  19% {
    transform: rotate(-20deg);
  }
  21% {
    transform: rotate(18deg);
  }
  23% {
    transform: rotate(-16deg);
  }
  25% {
    transform: rotate(14deg);
  }
  27% {
    transform: rotate(-12deg);
  }
  29% {
    transform: rotate(10deg);
  }
  31% {
    transform: rotate(-8deg);
  }
  33% {
    transform: rotate(6deg);
  }
  35% {
    transform: rotate(-4deg);
  }
  37% {
    transform: rotate(2deg);
  }
  39% {
    transform: rotate(-1deg);
  }
  41% {
    transform: rotate(1deg);
  }

  43% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(0);
  }
}
</style>
